import { useSidebar } from "@/components/ui/shadcn/Sidebar/Sidebar";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import { useUser } from "@/components/UserProvider";
import { getUserSessionQueryOptions } from "@/lib/data/react-query/auth/auth";
import { formatUsersName } from "@/lib/utils/format";
import { signOut } from "@aws-amplify/auth";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, LogOut, User } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../shadcn/DropdownMenu/DropdownMenu";

/** Matches `Sidebar` desktop `transition-[width] duration-200`. */
const SIDEBAR_WIDTH_TRANSITION_MS = 200;

type UserProfileDropdownProps = {
  forceCompact?: boolean;
  showIdentity?: boolean;
};

export function UserProfileDropdown({
  forceCompact = false,
  showIdentity = false,
}: UserProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { setOpenMobile, state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const shouldUseCompactStyle = forceCompact || isCollapsed;
  const [showExpandedProfile, setShowExpandedProfile] = useState(
    () => !shouldUseCompactStyle,
  );

  useEffect(() => {
    if (shouldUseCompactStyle) {
      setShowExpandedProfile(false);
      return;
    }
    const id = window.setTimeout(() => {
      setShowExpandedProfile(true);
    }, SIDEBAR_WIDTH_TRANSITION_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [shouldUseCompactStyle]);

  const { currentUser } = useUser();
  const name = formatUsersName(currentUser);
  const firmName = currentUser.firm.name;

  const queryClient = useQueryClient();

  const handleLogout = async () => {
    await signOut();
    await queryClient.cancelQueries();
    queryClient.clear();
    await queryClient.ensureQueryData(getUserSessionQueryOptions());
    setTimeout(() => {
      window.location.href = "/login";
    }, 250);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      {shouldUseCompactStyle ? (
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex shrink-0 cursor-default items-center rounded-md transition-colors hover:bg-neutral-200 dark:hover:bg-white/10",
              showIdentity
                ? "h-10 max-w-[230px] gap-2 px-2"
                : "h-8 w-8 justify-center",
            )}
          >
            <UserProfilePhoto user={currentUser} className="h-7 w-7" />
            {showIdentity ? (
              <div className="flex min-w-0 flex-col items-start leading-tight">
                <span className="w-full truncate text-xs font-medium">
                  {name}
                </span>
                <span className="text-muted-foreground w-full truncate text-xs">
                  {firmName}
                </span>
              </div>
            ) : null}
          </button>
        </DropdownMenuTrigger>
      ) : (
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              "flex h-[60px] min-w-0 cursor-pointer items-center rounded-md bg-neutral-100 py-2 transition-colors hover:bg-neutral-200 dark:bg-white/5 dark:hover:bg-white/10",
              showExpandedProfile
                ? "justify-between gap-2 px-3"
                : "justify-center px-1",
            )}
          >
            <div
              className={cn(
                "flex min-w-0 items-center",
                showExpandedProfile ? "flex-1 gap-3" : "justify-center",
              )}
            >
              <UserProfilePhoto
                user={currentUser}
                className="h-10 w-10 shrink-0"
              />
              {showExpandedProfile ? (
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm whitespace-nowrap">
                    Welcome back,
                  </span>
                  <span className="truncate text-sm font-semibold">{name}</span>
                </div>
              ) : null}
            </div>
            {showExpandedProfile ? (
              <div className="flex shrink-0 flex-col">
                <ChevronUp className="h-3 w-3" />
                <ChevronDown className="h-3 w-3" />
              </div>
            ) : null}
          </div>
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent
        align="end"
        className="mx-2 w-[272px] drop-shadow-md"
      >
        <DropdownMenuItem asChild>
          <Link
            to="/profile"
            onClick={() => {
              setOpenMobile(false);
            }}
          >
            <User className="mr-2 h-4 w-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            void handleLogout();
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
